import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import "../styles/sidebar.css";
import { clearAuth, getClientName, getDisplayName, getDisplayUsername } from "../lib/api";
import { getEnabledClientNavGroups } from "../lib/clientFeatures";

function navClassName({ isActive }) {
  return isActive ? "active" : "";
}

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const role = localStorage.getItem("role");

  const handleLogout = () => {
    clearAuth();
    navigate("/login");
  };

  const isAdmin = role === "admin";
  const clientGroups = useMemo(() => getEnabledClientNavGroups(), []);
  const [openGroups, setOpenGroups] = useState({});
  const [isCollapsed, setIsCollapsed] = useState(() => localStorage.getItem("sidebar_collapsed") === "true");
  const displayName = String(getDisplayName() || "").trim();
  const displayUsername = String(getDisplayUsername() || "").trim();
  const clientName = String(getClientName() || "").trim();
  const clientDashboardTitle = displayName || displayUsername || clientName || "Client";

  useEffect(() => {
    if (isAdmin) return;
    setOpenGroups((prev) => {
      const next = { ...prev };
      let changed = false;
      clientGroups.forEach((group) => {
        const shouldBeOpen = group.items.some((item) => item.to === location.pathname);
        if (!(group.label in next)) {
          next[group.label] = shouldBeOpen;
          changed = true;
        } else if (shouldBeOpen && !next[group.label]) {
          next[group.label] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [clientGroups, isAdmin, location.pathname]);

  useEffect(() => {
    document.documentElement.dataset.sidebarCollapsed = isCollapsed ? "true" : "false";
    localStorage.setItem("sidebar_collapsed", String(isCollapsed));
  }, [isCollapsed]);

  const toggleGroup = (label) => {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <aside className={`sidebar${isCollapsed ? " is-collapsed" : ""}`}>
      <button
        type="button"
        className="sidebar-toggle"
        onClick={() => setIsCollapsed((collapsed) => !collapsed)}
        aria-label={isCollapsed ? "Expand navigation menu" : "Collapse navigation menu"}
        aria-expanded={!isCollapsed}
      >
        <span aria-hidden="true">{isCollapsed ? ">" : "<"}</span>
      </button>

      <div className="sidebar-content">
        <div className="sidebar-title">{isAdmin ? "Admin Panel" : `${clientDashboardTitle} Dashboard`}</div>

        {isAdmin ? (
          <nav className="sidebar-nav">
            <NavLink to="/portal-admin/dashboard" className={navClassName}>Platform Overview</NavLink>
            <NavLink to="/portal-admin/clients" className={navClassName}>Manage Clients</NavLink>
            <NavLink to="/portal-admin/ops-alerts" className={navClassName}>Ops Alerts</NavLink>
            <NavLink to="/dashboard" className={navClassName}>Open Client Workspace</NavLink>
          </nav>
        ) : (
          <nav className="sidebar-nav">
            <NavLink to="/dashboard" end className={navClassName}>
              Overview
            </NavLink>

            {clientGroups.map((group) => {
              const isOpen = Boolean(openGroups[group.label]);
              const hasActiveChild = group.items.some((item) => item.to === location.pathname);
              return (
                <div key={group.label} className={`sidebar-group${isOpen ? " open" : ""}${hasActiveChild ? " active" : ""}`}>
                  <button
                    type="button"
                    className="sidebar-group-toggle"
                    onClick={() => toggleGroup(group.label)}
                    aria-expanded={isOpen}
                  >
                    <span>{group.label}</span>
                    <span className="sidebar-group-chevron">{isOpen ? "-" : "+"}</span>
                  </button>

                  {isOpen ? (
                    <div className="sidebar-group-links">
                      {group.items.map((item) => (
                        <NavLink key={item.to} to={item.to} className={navClassName}>
                          {item.label}
                        </NavLink>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </nav>
        )}

        <button className="logout-btn" onClick={handleLogout}>
          Logout
        </button>
      </div>
    </aside>
  );
}
